import {ValueListener} from "./interfaces";
import { Observable } from 'rxjs/Observable';
import { EventSource } from '../eventSite/eventSource';
import {observeProcessedEventAclRole} from "./observeProcessedEventAclRole";

export interface PageSetting {
    key: string,
    isEnabled: boolean,
    isPublic: boolean,
    hasContent: boolean,
    isPresentable: boolean,
    unlocked: boolean,
    sortKey ?: string
}

// PageSettings for every enabled Page;
//   disabled ones will not be present
export interface PageSettings {
    welcome?: PageSetting;
    story?: PageSetting;
    tidbits?: PageSetting;
    schedule?: PageSetting;
    travel?: PageSetting;
    faq?: PageSetting;
    vip?: PageSetting;
    guests?: PageSetting;
    registry?: PageSetting;
    moments?: PageSetting;
    RSVP?: PageSetting;
}

// based upon 'joy.git:modules/app-server-api/handlers/gqlResolvers/event/settings/pages/PagesResolver.js'
//   the canonical 'Page Settings' business logic
// related to 'event_service.git:src/service/firebase/importer/event.page.ts'
const DEFAULT_IS_ENABLED = new Set<string>([ "welcome", "moments", "RSVP" ]);
const DEFAULT_IS_PUBLIC = new Set<string>([ "welcome", "guests", "RSVP" ]);
const DEFAULT_HAS_CONTENT = new Set<string>([ "welcome", "guests", "moments", "RSVP" ]);
const DEFAULT_PAGE_ORDER = {
    "welcome":" 0",
    "story":" 1", // + "tidbits"
    "schedule":" 2",
    "travel":" 3",
    "faq": " 4",
    "vip":" 5",
    "guests":" 6",
    "registry":" 7",
    "moments":" 8",
    "RSVP": " 9",
};
const UNSPECIFIED_CONTENT = Object.freeze({});
const PAGE_SETTINGS_KEYS = [
    ...Object.keys(DEFAULT_PAGE_ORDER),
    "tidbits",
];


function _pageKey(settingsKey: string) {
    return ((settingsKey === "tidbits") ? "story" : settingsKey);
}

function _isEnabledHandler(maybePageConfig: Record<string, any> | undefined) {
    // `pages.config` = "I want to add / remove this Page (feature) from my Admin Dashboard"
    const pageConfig = maybePageConfig || {};

    return function _isEnabled(settingsKey: string): boolean {
        const pageKey = _pageKey(settingsKey);

        let keyConfig = pageConfig[pageKey];
        if (keyConfig === undefined) {
            if (pageKey === "RSVP") {
                // prior "old-school" versions used 'rsvp'
                keyConfig = pageConfig["rsvp"];
            }
        }
        if (keyConfig === undefined) {
            return DEFAULT_IS_ENABLED.has(pageKey) || false;
        }

        return !! keyConfig;
    }
}

function _isPublicHandler(maybeSecurity: Record<string, any>) {
    const security = maybeSecurity || {};
    const pageSecurity = security.pages || {};
    const userSecurity = security.users || {};
    const channelSecurity = security.channels || {};
    return function _isPublic(settingsKey: string): boolean {
        const pageKey = _pageKey(settingsKey);
        const keySecurity = pageSecurity[pageKey];

        switch (pageKey) {
            case "moments":
                // if a moments node is locked, channelSettings.moments.read === undefined
                // if a moments node is public, channelSettings.moments.read === true
                // even if I have no moments, the moments node will be present
                return (
                    (channelSecurity.engagementMoments && channelSecurity.engagementMoments.read) ||
                    (channelSecurity.moments && channelSecurity.moments.read)
                );
            case "RSVP":
                return (! userSecurity.lockedRSVP);
            default:
                return (
                    (keySecurity && keySecurity.read) ||
                    DEFAULT_IS_PUBLIC.has(pageKey) ||
                    false
                );
        }
    }
}

function _hasContentHandler(maybePageVisibility: Record<string, any> | undefined) {
    // `pages.visibility` = "I want to display this Page to my Guests"
    //   in reality, it's more like:  "There is content to display for this Page"
    //   moreover, it is *NOT* authoritative; it doesn't reliably predict "having content".
    //   most of its properties are Objects, which contain contextual boolean flags.
    //   also, there are real-world examples of `{ moments: false }`, eg. Boolean, property(s).
    // HOWEVER ...
    //   we will trust it, for now,
    //   and assume there is content (due to Client-injected defaults) for specific Pages
    //   even if that's not called out in `pages.visibility`
    const pageVisibility = maybePageVisibility || {};

    return function _hasContent(settingsKey: string): boolean {
        const pageKey = _pageKey(settingsKey);
        const keyConfig = pageVisibility[pageKey] || UNSPECIFIED_CONTENT;
        if (typeof keyConfig !== 'object') {
            // we don't trust that `{ moments: false }` means "no content"
            return true;
        }
        let hasContent: boolean | undefined;

        switch (settingsKey) { // vs. `pageKey`
            // (story is structured as pageSettings.story.custom and pageSettings.story.tidbits)
            // (if either has no data, it's undefined; pageSettings.story.custom === undefined)
            case "story":
                // `pages.visibility.story.custom`
                hasContent = keyConfig.custom || false;
                break;
            case "tidbits":
                // `pages.visibility.story.qandas`
                hasContent = keyConfig.qandas || false;
                break;
            case "registry":
                // not stored in Firebase; we can't be authoritative
                break;
            default:
                const keys = Object.keys(keyConfig);
                if (keyConfig === UNSPECIFIED_CONTENT) {
                    hasContent = DEFAULT_HAS_CONTENT.has(pageKey);
                }
                else {
                    // presence of any `true` => content
                    hasContent = keys.some((key) => (keyConfig[key] === true));
                }
        }
        return (hasContent === undefined) || (!! hasContent);
    }
}

function _pageOrderHandler(maybePageOrder: Record<string, string> | undefined) {
    const pageOrder = {
        ...DEFAULT_PAGE_ORDER,
        ...maybePageOrder,
    };
    return function _pageOrder(settingsKey: string): string {
        const pageKey = _pageKey(settingsKey);
        return pageOrder[pageKey] || undefined;
    }
}


export const observePageSettings = (eventDataSource: any, listener: ValueListener<PageSettings>) => {

    listener = eventDataSource.wrapListener(listener);

    let processedRole, securitySettings, pageVisibilitySettings, pageOrderSettings, pageConfig;

    const compute = function () {
        const _isEnabled = _isEnabledHandler(pageConfig);
        const _isPublic = _isPublicHandler(securitySettings);
        const _hasContent = _hasContentHandler(pageVisibilitySettings);
        const _pageOrder = _pageOrderHandler(pageOrderSettings);
        const pageSettings: PageSettings = {};

        PAGE_SETTINGS_KEYS.forEach(function (key) {
            const isEnabled = _isEnabled(key);
            const isPublic = _isPublic(key);
            const hasContent = _hasContent(key);

            const isPresentable = (isEnabled && hasContent);
            const unlocked = (isPresentable && (processedRole.isMember || isPublic));
            const sortKey = _pageOrder(key);
            pageSettings[key] = { key, isEnabled, isPublic, hasContent, isPresentable, unlocked, sortKey };
        });

        return pageSettings;
    };

    const onUpdate = function () {
        var anyUndef = ([securitySettings,
            pageVisibilitySettings,
            pageOrderSettings,
            pageConfig,
            processedRole].filter(x => (typeof x == "undefined")).length > 0);

        if (anyUndef) {
            return;
        }

        listener.onValue(compute() as PageSettings);
    };


    observeProcessedEventAclRole(eventDataSource, {onValue: function (r) {
        processedRole = r;
        onUpdate();
    }});

    eventDataSource.observeSecuritySettings({onValue: function (settings) {
        securitySettings = settings;
        onUpdate();
    }});

    eventDataSource.observePageConfig({onValue: function (config) {
        pageConfig = config;
        onUpdate();
    }});

    eventDataSource.observePageVisibilities({onValue: function (visibilities) {
        pageVisibilitySettings = visibilities;
        onUpdate();
    }});

    eventDataSource.observePageOrder({onValue: function (order) {
        pageOrderSettings = order;
        onUpdate();
    }});
};

export const observePageSettings$ = (eventSource: EventSource): Observable<PageSettings> => {
    const ds = eventSource.getDatasource();

    return new Observable((observer) => {
        const listener = {
            onValue: (val) => observer.next(val),
            onError: (err) => ((err.code !== "PERMISSION_DENIED") ? observer.error(err) : null),
        };
        observePageSettings(ds, listener);

        observer.add(() => {
            ds._observers.splice(ds._observers.indexOf(observer), 1);
        });
    }).publishReplay().refCount();
}
